import { FiZap, FiFileText, FiCalendar, FiPercent, FiStar } from "react-icons/fi";
import type { IconType } from "react-icons";
import { useInView } from "../../hooks/useInView";
import { siteConfig } from "../../config/site";

interface LoanOffer {
  key: string;
  badge: string;
  title: string;
  description: string;
  features: { icon: IconType; label: string }[];
  featureIconBg: string;
  iconColor: string;
  ctaLabel: string;
  ctaBg: string;
}

const LOAN_OFFERS: LoanOffer[] = [
  {
    key: "short-term",
    badge: "SHORT-TERM LOAN",
    title: "For immediate needs",
    description: "Get quick access to funds for your short-term needs.",
    features: [
      { icon: FiZap, label: "Quick and simple application" },
      { icon: FiFileText, label: "Minimal documentation" },
      { icon: FiCalendar, label: "Flexible repayment options" },
    ],
    featureIconBg: "#f3edff",
    iconColor: "#7c3aed",
    ctaLabel: "Explore Short-Term Loans",
    ctaBg: "#570380",
  },
  {
    key: "top-up",
    badge: "TOP-UP LOAN",
    title: "Easy top-up, better benefits",
    description:
      "Have a good repayment history? Get a quick top-up with better interest rates.",
    features: [
      { icon: FiPercent, label: "Lower interest rates for repeat customers" },
      { icon: FiZap, label: "Quick and easy top-up" },
      { icon: FiStar, label: "Higher loan eligibility" },
    ],
    featureIconBg: "#d1fae5",
    iconColor: "#059669",
    ctaLabel: "Explore Top-Up Loans",
    ctaBg: "#176766",
  },
];

export function LoanOptions() {
  const { ref, inView } = useInView<HTMLDivElement>();

  return (
    <section
      className="w-full px-4 py-16 sm:py-20"
      style={{
        background: "linear-gradient(180deg, #0a4a4a 0%, #042323 100%)",
      }}
    >
      <div className="mx-auto w-full max-w-[1376px]">
        {/* Header row */}
        <div
          ref={ref}
          className={`flex flex-col items-start justify-between gap-6 lg:flex-row lg:items-center ${inView ? "in-view" : ""}`}
        >
          <div className="flex max-w-[420px] flex-col items-start gap-4">
            <span className="reveal-scroll delay-1 rounded-full bg-[#f3ecfb] px-4 py-[6px] text-xs font-semibold uppercase tracking-[0.6px] text-brand-purple">
              Our Loan Options
            </span>
            <h2 className="reveal-scroll delay-2 text-3xl font-extrabold leading-tight text-white sm:text-4xl">
              Loans for Every Need
            </h2>
          </div>

          <p className="reveal-scroll delay-3 max-w-[420px] text-base leading-6 text-white/80">
            Whether it's an unexpected expense, a planned purchase or simply
            a little extra breathing room — GenZ Paisa helps you get the
            funds you need with a simple digital loan journey.
          </p>
        </div>

        {/* Cards */}
        <div className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-2">
          {LOAN_OFFERS.map((offer) => (
            <div
              key={offer.key}
              className="flex flex-col rounded-[20px] bg-[#fbfdff] p-6 shadow-[0px_8px_24px_rgba(0,0,0,0.15)] sm:p-[41px]"
            >
              <span
                className="w-fit rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.6px]"
                style={{ backgroundColor: offer.featureIconBg, color: offer.iconColor }}
              >
                {offer.badge}
              </span>
              <h3 className="pt-3 text-2xl font-bold leading-tight text-[#1c1917]">
                {offer.title}
              </h3>
              <p className="pt-2 text-sm leading-5 text-[#64748b]">
                {offer.description}
              </p>

              <ul className="mt-6 flex flex-1 flex-col gap-4">
                {offer.features.map((feature) => (
                  <li key={feature.label} className="flex items-center gap-4">
                    <span
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
                      style={{ backgroundColor: offer.featureIconBg }}
                    >
                      <feature.icon className="h-4 w-4" style={{ color: offer.iconColor }} />
                    </span>
                    <span className="text-sm font-medium text-[#334155]">
                      {feature.label}
                    </span>
                  </li>
                ))}
              </ul>

              <a
                href={siteConfig.applyLink || undefined}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-8 flex h-[54px] w-full items-center justify-center gap-2 rounded-full text-sm font-semibold text-white shadow-[0px_1px_1px_rgba(0,0,0,0.05)] transition-opacity hover:opacity-90"
                style={{ backgroundColor: offer.ctaBg }}
              >
                {offer.ctaLabel}
                <span aria-hidden="true">→</span>
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
